
import { trackEvent } from '@@/bits/analytics'
import { PiniaMapAction, PiniaMapState } from '@@/bits/pinia'
import { checkIfUserIsInZoomCollaborateMode, openUrlInBrowserFromZoom } from '@@/bits/zoom'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { useSlideshowStore } from '@@/pinia/slideshow'
import { useSurfaceActivityPanelStore } from '@@/pinia/surface_activity_panel'
import { useSurfaceFreezePadletPanel } from '@@/pinia/surface_freeze_padlet_panel'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import device from 'bits/device'
import window from 'bits/global'
import { navigateTo } from 'bits/location'
import { isRegistered } from 'bits/user_model'
import { Action, Component, Getter, State, Vue, namespace } from 'bits/vue'
import OzAvatar from 'library/v4/components/OzAvatar.vue'
import OzBaseButton from 'library/v4/components/OzBaseButton.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzIconButton, { OzIconButtonColorScheme, OzIconButtonSizePreset } from 'library/v4/components/OzIconButton.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from 'library/v4/components/OzPlainButton.vue'
import type { PopoverAnchor } from 'library/v4/components/OzPopoverModal.vue'
import { getAnchorFromElement } from 'library/v4/components/OzPopoverModal.vue'
import SearchIconWithIndicator from 'library/v4/components/SearchIconWithIndicator.vue'
import { debounce } from 'lodash-es'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfaceFilterStore } from 'pinia/surface_filter'
import { useSurfacePostSearchStore } from 'pinia/surface_post_search'
import { useSurfaceSharePanelStore } from 'pinia/surface_share_panel'

import type { User, Wall } from 'types'
import AppBarAccountAvatar from 'vuecomponents/AppBarAccountAvatar.vue'
import SurfaceActionBarMoreMenu from 'vuecomponents/SurfaceActionBarMoreMenu.vue'
import type { ConfirmationDialogPayload } from 'vuexstore/modules/global_confirmation_dialog'

const postStore = namespace('post')
const settingsStore = namespace('settings')
const surfaceSharePanelStore = useSurfaceSharePanelStore()

const POPOVER_WIDTH = 256
const ONBOARDING_PANEL_OFFSET = POPOVER_WIDTH + 13

@Component({
  components: {
    OzAvatar,
    OzBaseButton,
    OzIconButton,
    OzPlainButton,
    OzIcon,
    SurfaceActionBarMoreMenu,
    SearchIconWithIndicator,
    AppBarAccountAvatar,
  },
})
export default class SurfaceActionBar extends Vue {
  $refs!: {
    moreButton: any
  }

  device = device

  OzIconButtonColorScheme = OzIconButtonColorScheme
  OzIconButtonSizePreset = OzIconButtonSizePreset
  OzPlainButtonColorScheme = OzPlainButtonColorScheme
  OzPlainButtonSizePreset = OzPlainButtonSizePreset

  @State user!: User
  @State isElectronApp!: boolean
  @State isUserHostingZoomCollaboration!: boolean
  @Getter isMap!: boolean
  @Getter canIAdminister!: boolean
  @Getter isPostable!: boolean
  @Getter links!: Record<string, string>
  @Getter loginAndRedirectToWallUrl?: string
  @Getter dir!: string
  @Getter isDesktopLayout!: boolean
  @Getter isMobileLayout!: boolean
  @Getter canIRemake!: boolean
  @Getter isRemakeable!: boolean
  @Getter skinPrimaryVariantColorAsRGB!: string | null
  @Getter skinPrimaryTextColor!: 'black' | 'white' | null
  @Getter skinAccentColorAsRGB!: string | null
  @Getter skinAccentTextColor!: 'black' | 'white' | null
  @Getter xActionBarMoreMenu!: boolean
  @Getter isSectionBreakout!: boolean
  @Action showActionBarMoreMenu!: () => void
  @Action hideActionBarMoreMenu!: () => void
  @PiniaMapState(useDarkModeStore) isBrowserDarkMode!: ReturnType<typeof useDarkModeStore>['isBrowserDarkMode']
  @Action showRemakePanel!: () => void
  @Action showPromptToLoginDialog!: (payload: Partial<ConfirmationDialogPayload>) => void
  @PiniaMapState(useSurfaceActivityPanelStore) xActivityPanel!: boolean
  @PiniaMapAction(useSurfaceActivityPanelStore) showActivityPanel!: () => void
  @PiniaMapAction(useSurfaceActivityPanelStore) closeActivityPanel!: () => void
  @PiniaMapAction(useSurfaceMapStore) startPickingLocation!: ReturnType<
    typeof useSurfaceMapStore
  >['startPickingLocation']

  @postStore.Action startNewPost!: () => void
  @settingsStore.Action toggleSettingsPanel!: () => void
  @PiniaMapState(useSurfaceStore) wallAttributes!: Partial<Wall>
  @PiniaMapState(useSurfaceStore) isFrozen!: boolean
  @PiniaMapAction(useSurfaceFreezePadletPanel) showSurfaceFreezePadletPanel!: () => void
  @PiniaMapState(useSlideshowStore) coverPageUrl!: string
  @PiniaMapState(useSurfaceOnboardingPanelStore) showOnboardingPanel!: boolean
  @PiniaMapState(useSurfaceOnboardingPanelStore) xGalleryOnboardingPanelMobile!: boolean

  get isSkinColorAvailable(): boolean {
    return (
      !!this.skinPrimaryTextColor &&
      !!this.skinPrimaryVariantColorAsRGB &&
      !!this.skinAccentColorAsRGB &&
      !!this.skinAccentColorAsRGB
    )
  }

  get useSkinBasedColorsForButtons(): boolean {
    // on mobile the buttons are just "Clear"
    return this.isSkinColorAvailable && !this.isMobileLayout
  }

  get buttonDarkMode(): boolean | 'auto' {
    if (!this.isSkinColorAvailable) {
      return this.isBrowserDarkMode
    }
    // skinPrimaryTextColor is the color for text/button icon when shown on top of the skin's primary color
    // darkMode means dark background and light icons
    // => buttonDarkMode  should be true when we want white text color
    return this.skinPrimaryTextColor === 'white'
  }

  get buttonColorScheme(): OzIconButtonColorScheme {
    if (this.isSectionBreakout) {
      return OzIconButtonColorScheme.Transparent
    }

    if (this.useSkinBasedColorsForButtons) {
      return OzIconButtonColorScheme.Bare
    }

    return this.isMobileLayout ? OzIconButtonColorScheme.SecondaryClear : OzIconButtonColorScheme.Secondary
  }

  get activeButtonColorScheme(): OzIconButtonColorScheme {
    if (this.useSkinBasedColorsForButtons) {
      return OzIconButtonColorScheme.Bare
    }

    return this.isMobileLayout ? OzIconButtonColorScheme.SecondaryClearActive : OzIconButtonColorScheme.SecondaryActive
  }

  get buttonSkinBasedTailwindClasses(): Record<string, boolean> {
    if (this.isSectionBreakout) {
      return {}
    }

    if (this.isMobileLayout) {
      return {
        'disabled:!bg-transparent': true,
      }
    }

    if (!this.isSkinColorAvailable) {
      return {}
    }

    // buttonDarkMode is either true or false here (cannot be 'auto') since we checked for this.isSkinColorAvailable
    return {
      // Base
      'bg-grey-1000 bg-opacity-5': this.buttonDarkMode === false,
      'bg-white-100 bg-opacity-10': this.buttonDarkMode === true,
      'text-dark-text-200': this.buttonDarkMode === false,
      'text-light-text-200': this.buttonDarkMode === true,

      // Hovered
      'hover-hover:hover:bg-opacity-10': this.buttonDarkMode === false,
      'hover-hover:hover:bg-opacity-15': this.buttonDarkMode === true,

      // Disabled
      'disabled:!bg-highway-400': this.buttonDarkMode === false,
      'disabled:!bg-white-disabled': this.buttonDarkMode === true,
    }
  }

  get activeButtonSkinBasedTailwindClasses(): Record<string, boolean> {
    if (!this.useSkinBasedColorsForButtons) {
      return {}
    }

    // buttonDarkMode is either true or false here (cannot be 'auto') since we checked for this.isSkinColorAvailable
    return {
      // Base
      'bg-grey-1000 bg-opacity-5': this.buttonDarkMode === false,
      'bg-white-100 bg-opacity-10': this.buttonDarkMode === true,
      'text-grape-500': this.buttonDarkMode === false,
      'text-canary-500': this.buttonDarkMode === true,

      // Hovered
      'hover-hover:hover:bg-opacity-10': this.buttonDarkMode === false,
      'hover-hover:hover:bg-opacity-15': this.buttonDarkMode === true,

      // Disabled
      'disabled:!bg-highway-400': this.buttonDarkMode === false,
      'disabled:!bg-white-disabled': this.buttonDarkMode === true,
    }
  }

  get isUserRegistered(): boolean {
    return isRegistered(this.user)
  }

  get isRemakeDisabled(): boolean {
    if (!this.isUserRegistered) {
      // Check isRemakeable as canIRemake is always false for unregistered users
      // and we want the remake button to be clickable (for padlets with remakes enabled)
      // so the login dialog can be shown
      return !this.isRemakeable
    }
    return !this.canIRemake
  }

  /**
   * MOBILE
   * - [ Share, Activity, +, Search, ... ]
   *
   * MOBILE ZOOM APP
   * - [ Slideshow, Activity, +, Search, Open in browser ]
   *
   *
   * DESKTOP
   * - [ Share, Remake, Activity, Slideshow, Settings, ... ]
   *
   * DESKTOP ZOOM APP
   * - [ Activity, Slideshow, Settings, Open in browser ]
   */

  @PiniaMapState(useSurfacePostSearchStore) searchTerm!: string
  @PiniaMapState(useSurfaceFilterStore) hasSelectedFilters!: boolean

  get xSearchIndicator(): boolean {
    return this.searchTerm !== '' || this.hasSelectedFilters
  }

  showSharePanel() {
    surfaceSharePanelStore.xSurfaceSharePanel = true
  }

  showSearchBar() {
    useSurfaceStore().openSearchBar()
  }

  async openPadletInBrowser() {
    openUrlInBrowserFromZoom(this.links.show)
  }

  openSlideshow() {
    trackEvent('Surface', 'Opened slideshow')
    if (!this.links.show) {
      return
    }

    if (device.zoomApp) {
      navigateTo(this.coverPageUrl)
      return
    }

    navigateTo(this.coverPageUrl, { target: '_blank' })
  }

  showRequestLoginToRemakeDialog(): void {
    this.showPromptToLoginDialog({
      title: this.__('You must be logged in to remake this padlet'),
    })
  }

  isLoginAndRedirectToWallUrlExpired = false

  /**
   * ZOOM
   */
  isUserInZoomCollaborationMode = false

  get isZoomCollaborationGuest(): boolean {
    return this.isUserInZoomCollaborationMode === true && this.isUserHostingZoomCollaboration === false
  }

  /**
   * SHOW/HIDE MORE MENU
   */

  openMoreMenu(): void {
    if (!this.moreMenuAnchor.top || !this.moreMenuAnchor.left) {
      this.updateMoreMenuAnchor()
    }
    this.showActionBarMoreMenu()
  }

  closeMoreMenu(): void {
    this.hideActionBarMoreMenu()
  }

  /**
   * MORE MENU POSITIONING
   */

  moreMenuAnchor: Partial<PopoverAnchor> = {}

  updateMoreMenuAnchor(): void {
    const moreButton = this.$refs.moreButton?.$el
    if (!moreButton) return
    const anchor = getAnchorFromElement(moreButton, {
      top: 0,
      left: this.isMobileLayout
        ? this.dir === 'rtl'
          ? -15
          : this.showOnboardingPanel
          ? 12 - 160 // numbers by experimentation
          : 12
        : this.dir === 'rtl'
        ? this.showOnboardingPanel
          ? -12 + ONBOARDING_PANEL_OFFSET
          : -12
        : this.showOnboardingPanel
        ? -39 - ONBOARDING_PANEL_OFFSET
        : -39,
    })
    this.moreMenuAnchor = {
      ...anchor,
      position: 'side',
    }
  }

  debouncedUpdateMoreMenuAnchor = debounce(() => {
    if (window.visualViewport && window.visualViewport.scale > 1) {
      // When the content is pinch-zoomed, prevented any resizing and recalculation.
      return
    }
    this.updateMoreMenuAnchor()
  }, 500)
  /**
   * COMPONENT LIFECYCLE
   */

  async mounted(): Promise<void> {
    if (this.loginAndRedirectToWallUrl) {
      // Refer to DEEPLINK_RESPONSE_WINDOW_DURATION_WITH_BUFFER in app/services/lti_launch_context.rb
      const TIMEOUT_MS = 15 * 60 * 1000
      setTimeout(() => (this.isLoginAndRedirectToWallUrlExpired = true), TIMEOUT_MS)
    }
    window.addEventListener('resize', this.debouncedUpdateMoreMenuAnchor)
    this.isUserInZoomCollaborationMode = await checkIfUserIsInZoomCollaborateMode()
  }

  beforeDestroy(): void {
    window.removeEventListener('resize', this.debouncedUpdateMoreMenuAnchor)
  }
}
